import React, { useState } from 'react';
import { usePatient, usePatientEvents } from '../../hooks/usePatient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { logVisit, logMeal, logDose } from '../../api/careRecords';
import { MedicationCard } from '../../components/MedicationCard';
import { LoadingScreen } from '../../components/LoadingScreen';
import type { PatientEvent } from '../../api/patients';

interface CarerDashboardProps {
  patientId: number;
}

function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

export const CarerDashboard: React.FC<CarerDashboardProps> = ({ patientId }) => {
  const today = getTodayDate();
  const { data: patient, isLoading: patientLoading } = usePatient(patientId);
  const { data: events, isLoading: eventsLoading } = usePatientEvents(patientId);
  const queryClient = useQueryClient();
  const [visitLogged, setVisitLogged] = useState(false);

  const visitMutation = useMutation({
    mutationFn: () => logVisit(patientId),
    onSuccess: () => {
      setVisitLogged(true);
      queryClient.invalidateQueries({ queryKey: ['patientEvents', patientId] });
    },
  });

  const mealMutation = useMutation({
    mutationFn: ({ mealType }: { mealType: string }) => logMeal(patientId, mealType, 'eaten'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patientEvents', patientId] });
    },
  });

  const medicationMutation = useMutation({
    mutationFn: ({ medicationId, medicationTimeId }: { medicationId: number; medicationTimeId: number | null }) =>
      logDose(patientId, medicationId, medicationTimeId, 'given'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patientEvents', patientId] });
    },
  });

  if (patientLoading || eventsLoading) {
    return <LoadingScreen message="Loading patient data..." />;
  }

  const now = new Date();
  const overdueMedications = (events ?? []).filter(
    (e: PatientEvent) => e.type === 'medication' && new Date(`${today}T${e.time}:00`) < now,
  );

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: 16, fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ fontSize: 24, marginBottom: 4 }}>
        {patient?.first_name} {patient?.last_name}
      </h1>
      <p style={{ color: '#666', marginBottom: 24 }}>{today}</p>

      {!visitLogged && (
        <button
          onClick={() => visitMutation.mutate()}
          disabled={visitMutation.isPending}
          style={{
            width: '100%',
            padding: 16,
            fontSize: 18,
            fontWeight: 600,
            backgroundColor: '#2e7d32',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            marginBottom: 24,
          }}
        >
          {visitMutation.isPending ? 'Logging...' : 'Mark Visit Arrived'}
        </button>
      )}

      {visitLogged && (
        <p style={{ color: '#2e7d32', fontWeight: 600, marginBottom: 24 }}>
          ✓ Visit logged at {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}

      {overdueMedications.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, color: 'red', marginBottom: 12 }}>⚠ Overdue Medications</h2>
          {overdueMedications.map((e: PatientEvent) => (
            <MedicationCard key={e.id} name={e.title} dosage="As prescribed" time={e.time} status="overdue" />
          ))}
        </section>
      )}

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Today's Schedule</h2>
        {(events ?? []).map((event: PatientEvent) => (
          <div
            key={event.id}
            style={{
              padding: 12,
              backgroundColor: '#fff',
              borderRadius: 8,
              marginBottom: 8,
              borderLeft: `4px solid ${event.color}`,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>{event.title}</span>
            <span style={{ color: '#666' }}>{event.time}</span>
          </div>
        ))}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Quick Actions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(events ?? [])
            .filter((e: PatientEvent) => e.type === 'meal')
            .map((e: PatientEvent) => (
              <button
                key={e.id}
                onClick={() => mealMutation.mutate({ mealType: e.meal_type! })}
                disabled={mealMutation.isPending}
                style={{
                  padding: 12,
                  fontSize: 16,
                  backgroundColor: '#fff',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  color: '#1976d2',
                  cursor: 'pointer',
                }}
              >
                Log {e.title} eaten
              </button>
            ))}
          {(events ?? [])
            .filter((e: PatientEvent) => e.type === 'medication')
            .map((e: PatientEvent) => (
              <button
                key={e.id}
                onClick={() =>
                  medicationMutation.mutate({
                    medicationId: e.medication_id!,
                    medicationTimeId: e.medication_time_id ?? null,
                  })
                }
                disabled={medicationMutation.isPending}
                style={{
                  padding: 12,
                  fontSize: 16,
                  backgroundColor: '#fff',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  color: '#1976d2',
                  cursor: 'pointer',
                }}
              >
                Administer {e.title} at {e.time}
              </button>
            ))}
        </div>
      </section>
    </div>
  );
};
